import { WalkthroughStepState, WalkthroughStepStateProgress } from './walkthrough-step-state';

export interface WalkthroughStepAction {
    description: string;
}

export interface WalkthroughStepJSON {
    actions?: WalkthroughStepAction[];
    description: string;
    id: string;
    steps: WalkthroughStepJSON[];
    title: string;
}

export class WalkthroughStep {
    private _actions?: WalkthroughStepAction[];
    public get actions(): WalkthroughStepAction[] {
        if (this.isLeaf) { return this._actions; }
        if (this.steps.length === 0) { return []; }
        return this.steps
                   .filter(step => !step.done)
                   .map(step => step.actions )
                   .reduce((acc, actions) => {
                     const values: WalkthroughStepAction[] = actions ? actions : [];
                     return [...acc, ...values];
                   }, []);
    }
    public set actions(values: WalkthroughStepAction[]) {
        this._actions = values;
    }

    description: string;

    done: boolean;

    doneAt?: string;

    id: string;

    private _progress?: WalkthroughStepStateProgress;
    public get progress(): number|WalkthroughStepStateProgress {
        // If we have complex progress we won't calculate but just return
        if (this._progress) { return this._progress; }

        if (this.done) { return 1.0; }
        if (this.isLeaf) { return this.done ? 1.0 : 0.0; }

        const numberOfSteps = this.steps.length;
        if (numberOfSteps === 0) { return 0.0; }

        const sum = this.steps.reduce((acc, step) => {
            if (typeof step.progress === 'number') {
                return acc += step.progress;
            } else {
                return acc += step.progress.actual / step.progress.expected;
            }
        }, 0.0);
        return sum / numberOfSteps;
    }

    get isLeaf(): boolean {
        return this.steps === undefined;
    }

    steps?: WalkthroughStep[];

    title: string;

    constructor(id: string,
                title: string,
                description: string,
                done?: boolean,
                progress?: WalkthroughStepStateProgress,
                actions?: WalkthroughStepAction[],
                doneAt?: string,
                steps?: WalkthroughStep[]) {
        this.actions = actions === null ? undefined : actions;
        this.description = description;
        this.done = !!done;
        this.doneAt = doneAt === null ? undefined : doneAt;
        this.id = id;
        this._progress = progress === null ? undefined : progress;
        this.steps = steps === null ? undefined : steps;
        this.title = title;
    }

    public static stepFromJSON(json: WalkthroughStepJSON): WalkthroughStep {
        const step = new WalkthroughStep(json.id,
                                         json.title,
                                         json.description,
                                         false,
                                         null,
                                         json.actions,
                                         null);
        if (json.steps && json.steps.length > 0) {
            step.steps = json.steps.map(stepJSON => {
                return WalkthroughStep.stepFromJSON(stepJSON);
            });
        }
        return step;
    }

    //
    // State management
    //

    public applyState(state: WalkthroughStepState): void {
        if (this.id !== state.id) {
            throw new Error('State is not for this Step!');
        }

        this.done = state.done;
        this.doneAt = state.doneAt === null ? undefined : state.doneAt;
        this._progress = state.progress === null ? undefined : state.progress;

        if (state.steps) {
            if (this.steps.length !== state.steps.length) {
                throw new Error($localize `Step has ${this.steps.length} steps but State has ${state.steps.length}!`);
            }
            this.steps.forEach((step, index) => {
                step.applyState(state.steps[index]);
            });
        }
    }
}
